import { useState, useEffect, useMemo } from 'react'
import { fetchBlockPrices, fetchBlockFormats, upsertBlockPrice, fetchProducts, fetchPriceTiers } from '../supabase-client'
import { useDataCache } from '../contexts/DataCacheContext'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Check, X } from 'lucide-react'

export default function BlockPriceCatalogue() {
  const { invalidateCache } = useDataCache()
  const [blockPrices, setBlockPrices] = useState([])
  const [blockFormats, setBlockFormats] = useState([])
  const [products, setProducts] = useState([])
  const [priceTiers, setPriceTiers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingCell, setEditingCell] = useState(null) // "productId::tierId"
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [pricesData, formatsData, productsData, tiersData] = await Promise.all([
        fetchBlockPrices(),
        fetchBlockFormats(),
        fetchProducts(),
        fetchPriceTiers()
      ])
      setBlockPrices(pricesData)
      setBlockFormats(formatsData.filter(f => f.active))
      setProducts(productsData.filter(p => p.active))
      setPriceTiers(tiersData)
    } catch (err) {
      console.error('Failed to load block price catalogue:', err)
      setError('Failed to load data. Please refresh.')
    } finally {
      setLoading(false)
    }
  }

  // Build price lookup: "productId-tierId" → price_per_kg
  const priceMap = useMemo(() => {
    const map = new Map()
    blockPrices.forEach(bp => {
      map.set(`${bp.product_id}::${bp.price_tier_id}`, parseFloat(bp.price_per_kg))
    })
    return map
  }, [blockPrices])

  const handleCellClick = (productId, tierId) => {
    const key = `${productId}::${tierId}`
    const currentPrice = priceMap.get(key)
    setEditingCell(key)
    setEditValue(currentPrice !== undefined ? String(currentPrice) : '')
  }

  const handleSave = async () => {
    if (!editingCell || saving) return

    const [productId, tierId] = editingCell.split('::')
    const price = parseFloat(editValue)

    if (isNaN(price) || price < 0) {
      setEditingCell(null)
      return
    }

    try {
      setSaving(true)
      await upsertBlockPrice(productId, tierId, price)
      invalidateCache('blockPrices')

      // Update local state
      setBlockPrices(prev => {
        const existing = prev.find(bp => bp.product_id === productId && bp.price_tier_id === tierId)
        if (existing) {
          return prev.map(bp =>
            bp.product_id === productId && bp.price_tier_id === tierId
              ? { ...bp, price_per_kg: price }
              : bp
          )
        }
        return [...prev, { product_id: productId, price_tier_id: tierId, price_per_kg: price }]
      })
      setEditingCell(null)
    } catch (err) {
      console.error('Failed to save block price:', err)
      setError('Failed to save price.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditingCell(null)
    setEditValue('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave()
    if (e.key === 'Escape') handleCancel()
  }

  if (loading) return <div className="p-4 text-sm text-gray-500">Loading block prices...</div>

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            Block Price Catalogue
          </CardTitle>
          <CardDescription>
            Set price per kg for each strain at each pricing tier. Per-block costs are calculated automatically from format weights.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {products.length === 0 ? (
          <p className="text-sm text-gray-500">No active products found.</p>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Strain</TableHead>
                  {priceTiers.map(tier => (
                    <TableHead key={tier.id} className="min-w-[200px]">
                      {tier.display_name || tier.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map(product => (
                  <TableRow key={product.id}>
                    <TableCell className="font-medium">{product.strain}</TableCell>
                    {priceTiers.map(tier => {
                      const key = `${product.id}::${tier.id}`
                      const pricePerKg = priceMap.get(key)
                      const isEditing = editingCell === key

                      return (
                        <TableCell key={tier.id} className="p-2">
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="w-20 h-8 text-sm"
                                autoFocus
                              />
                              <button onClick={handleSave} disabled={saving} className="p-1 text-green-600 hover:text-green-800">
                                <Check className="h-4 w-4" />
                              </button>
                              <button onClick={handleCancel} className="p-1 text-gray-400 hover:text-gray-600">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div
                              onClick={() => handleCellClick(product.id, tier.id)}
                              className="cursor-pointer hover:bg-gray-50 rounded px-2 py-1 min-h-[2.5rem]"
                            >
                              {pricePerKg !== undefined ? (
                                <div>
                                  <div className="font-medium text-sm">£{pricePerKg.toFixed(2)}/kg</div>
                                  {blockFormats.length > 0 && (
                                    <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                                      {blockFormats.map(f => (
                                        <div key={f.id}>
                                          {f.name}: £{(pricePerKg * parseFloat(f.weight_kg)).toFixed(2)}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">Click to set</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
